import { useEffect, useState } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginUser } from '../store/slices/authSlice';
import logo from '../assets/img/logo.png';
import loginIllustration from '../assets/img/login/login.png';

const colorFlow = keyframes`
  0% { background-position: 0% 50%; opacity: 0; transform: translateY(12px); }
  40% { opacity: 1; transform: translateY(0); }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; opacity: 1; transform: translateY(0); }
`;

const AnimatedBrand = styled(Typography)(() => ({
  fontWeight: 700,
  letterSpacing: '-0.01em',
  backgroundImage:
    'linear-gradient(120deg, #10b981 0%, #0ea5e9 45%, #22d3ee 60%, #10b981 100%)',
  backgroundSize: '200% auto',
  color: 'transparent',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  animation: `${colorFlow} 6s ease 10s infinite`,
  display: 'inline-block'
}));

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, error, user } = useSelector((state) => state.auth);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (user) {
      const redirectTo = location.state?.from?.pathname || '/app-sources';
      navigate(redirectTo, { replace: true });
    }
  }, [user, navigate, location.state]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!username || !password) {
      setFormError('Username and password are required.');
      return;
    }
    setFormError('');
    const deviceInfo = typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown Device';
    dispatch(loginUser({ username, password, deviceInfo }));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'stretch',
        backgroundColor: '#FFFFFA'
      }}
    >
      <Box
        sx={{
          width: { xs: '100%', md: '50%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6, md: 8 },
          py: { xs: 0, md: 0 },
          minHeight: { xs: '100vh', md: 'auto' },
          flexGrow: 1
        }}
      >
        <Stack
          spacing={2}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            maxWidth: 420,
            backgroundColor: 'rgba(255,255,255,0.86)',
            boxShadow: '0px 24px 64px rgba(15,23,42,0.08)',
            borderRadius: 1,
            px: { xs: 3, sm: 5 },
            py: { xs: 4, sm: 6 },
            alignItems: 'center'
          }}
        >
          <AnimatedBrand variant="h5">
            MRT Ticket Engine
          </AnimatedBrand>
          <Box
            component="img"
            src={logo}
            alt="MRT Ticket Engine logo"
            sx={{ width: 88, height: 88, objectFit: 'contain' }}
          />
          <Box textAlign="center" sx={{ width: '100%' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Sign In
            </Typography>
            <Typography color="text.secondary">
              Enter your credentials to access the admin dashboard.
            </Typography>
          </Box>
          {formError && <Alert severity="error" sx={{ width: '100%' }}>{formError}</Alert>}
          {error && !formError && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Email"
            placeholder="admin@example.com"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Password"
            placeholder="Enter password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            fullWidth
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={status === 'loading'}
            sx={{ width: '100%', mt: 1 }}
          >
            {status === 'loading' ? (
              <CircularProgress size={24} sx={{ color: 'primary.contrastText' }} />
            ) : (
              'Sign In'
            )}
          </Button>
        </Stack>
      </Box>
      <Box
        sx={{
          width: { xs: '100%', md: '50%' },
          display: { xs: 'none', md: 'block' },
          position: 'relative',
          textAlign: 'center'
        }}
      >
        <Box
          component="img"
          src={loginIllustration}
          alt="MRT Ticket Engine hero"
          sx={{
            position: 'absolute',
            inset: 0,
            // width: '100%',
            height: '100%',
            objectFit: 'cover',
            margin: '0 auto'
            // backgroundPosition: 'center',
            // filter: 'drop-shadow(0px 24px 60px rgba(15,23,42,0.18))'
          }}
        />
      </Box>
    </Box>
  );
};

export default LoginPage;
